import { Component, OnDestroy, OnInit, output } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../services/api.services';
import { Client } from '../../../models/client';
import {
	FormControl,
	Validators,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { AppDialogWithButtonsComponent } from '../../../components/app-dialog-with-buttons/app-dialog-with-buttons.component';
import { SystemCapabilityService } from '../../../services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from '../../../models/data/systemCapability';
import { BannerMessageService } from '../../../services/banner-message.service';
import { ClientDetailsService } from '../../../services/client-details.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatTabGroup } from '@angular/material/tabs';
import { Subscription } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/list';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { NgClass } from '@angular/common';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { ContactsOfNotificationMainComponent } from '../../contacts-notification/contacts-of-notification-main/contacts-of-notification-main.component';

@Component({
	selector: 'app-client-about',
	templateUrl: './client-about.component.html',
	styleUrls: ['./client-about.component.scss', '../../common/main.scss'],
	imports: [
		MatButton,
		MatDivider,
		MatChipSet,
		MatChip,
		NgClass,
		MatFormField,
		MatSelect,
		FormsModule,
		ReactiveFormsModule,
		MatOption,
		MatInput,
		ContactsOfNotificationMainComponent,
	],
})
export class ClientAboutComponent implements OnInit, OnDestroy {
	tabSubscription?: Subscription;
	updateClientDetails = output<void>();

	canEditPublicInfo = false;
	editingPublicInfo = false;

	clientId = '';
	client!: Client;
	contactNotificationsEnabled = false;

	status = new FormControl<string>('');
	firstname = new FormControl<string>('', [
		Validators.required,
		Validators.minLength(1),
	]);
	lastname = new FormControl<string>('', [
		Validators.required,
		Validators.minLength(1),
	]);
	idn = new FormControl<string>('', [
		Validators.required,
		Validators.pattern('^[a-zA-Z0-9]*$'),
		Validators.minLength(1),
	]);

	constructor(
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly scService: SystemCapabilityService,
		private readonly clientDetailsService: ClientDetailsService,
		private readonly bannerMessageService: BannerMessageService,
		private readonly userProfileService: UserProfileService,
		private readonly tabGroup: MatTabGroup
	) {
		this.clientId = this.route.snapshot.params.id;
	}

	async ngOnInit(): Promise<void> {
		const [client, userProfile, sentrilockCompanyId] = await Promise.all([
			this.clientDetailsService.getClientDetails(this.clientId),
			this.userProfileService.getUserProfile(),
			this.apiService.getSentrilockCompanyId(),
		]);
		this.client = client;

		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
		const hasPropertyManagement = this.scService.has(
			SystemCapabilityEnum.propertyManagement
		);
		const loggedInCompanyIsSentriLock = activeCompanyId === sentrilockCompanyId;
		const companyOwnsClient = this.client.companyId === activeCompanyId;

		this.canEditPublicInfo =
			hasPropertyManagement &&
			(loggedInCompanyIsSentriLock || companyOwnsClient);

		// Check if contact notifications are enabled for the current company
		this.contactNotificationsEnabled =
			userProfile?.activeRoleDetails?.company?.contactNotifications ?? false;
	}

	getChipStyling(status: boolean): string {
		if (status) {
			return 'status-chip-active';
		} else {
			return 'status-chip-inactive';
		}
	}

	editPublicInfoButtonClicked(): void {
		this.editingPublicInfo = !this.editingPublicInfo;

		this.status.setValue(this.client?.active ? 'active' : 'inactive');
		this.firstname.setValue(this.client?.firstName ?? '');
		this.lastname.setValue(this.client?.lastName ?? '');
		this.idn.setValue(this.client?.idn ?? '');
	}

	editPublicInfoCancelButtonClicked(): void {
		this.editingPublicInfo = !this.editingPublicInfo;

		this.status.setValue(null);
		this.firstname.setValue(null);
		this.lastname.setValue(null);
		this.idn.setValue(null);
	}

	async savePublicInfoEdits(): Promise<void> {
		const updateClientPayload = {
			firstName: this.firstname.value,
			lastName: this.lastname.value,
			idn: this.idn.value,
			active: this.status.value === 'active',
			serialNumber: this.client?.serialNumber ?? '',
		};
		const response = await this.apiService.updateClientDetails(
			this.clientId,
			updateClientPayload
		);
		if ('installedDevices' in response && response?.installedDevices > 0) {
			const dialogConfig = new MatDialogConfig();
			dialogConfig.panelClass = 'dialog-container';
			dialogConfig.data = {
				title: $localize`:@@clientDetails-installedDevicesDialogTitle:Active Device Detected`,
				message: $localize`:@@clientDetails-installedDevicesDialogDescription:This client has a device currently installed at their property. To set them as inactive, you'll need to uninstall the device first.`,
				cancelButtonText: $localize`:@@clientDetails-installedDevicesDialogCancelButtonTitle:Cancel`,
				confirmButtonText: $localize`:@@clientDetails-installedDevicesDialogConfirmButtonTitle:View Installed Devices`,
			};
			let dialogRef = this.dialog.open(
				AppDialogWithButtonsComponent,
				dialogConfig
			);

			// Dialog close event listening for redirection or action :
			dialogRef?.afterClosed()?.subscribe((result) => {
				if (result && result.isConfirmButtonClicked) {
					this.tabSubscription = this.route.queryParamMap.subscribe(() => {
						this.tabGroup.selectedIndex = 2; // Navigate to the Installed Devices tab
					});
				}
			});
		}
		if ('affectedRows' in response && response?.affectedRows > 0) {
			this.client = await this.clientDetailsService.getClientDetails(
				this.clientId,
				true
			);
			this.updateClientDetails.emit();
			this.editingPublicInfo = !this.editingPublicInfo;
			this.bannerMessageService.sendMessage(
				$localize`:@@clientDetails-clientDetailsUpdateSuccessMessage:Client details updated successfully.`
			);
		}
	}

	ngOnDestroy(): void {
		this.tabSubscription?.unsubscribe();
		this.tabSubscription = undefined;
	}
}
